import { useState } from 'react';

import { appendDigit, canSubmitPin } from './pinEntry.js';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

interface PinPadProps {
  /** Called with the entered PIN when the operator confirms. */
  onSubmit: (pin: string) => void;
  /** Disable input while a submit is settling. */
  disabled?: boolean;
}

/**
 * Big touch-friendly PIN pad. The PIN never leaves this device except inside a
 * punch request to the server (verified server-side only); we hold it in local
 * component state just long enough to submit, then clear it.
 */
export function PinPad({ onSubmit, disabled = false }: PinPadProps): JSX.Element {
  const [pin, setPin] = useState('');

  // The RULES live in pinEntry.ts so they can be tested without a DOM — the kiosk
  // has no jsdom by design. This component is now only wiring.
  const append = (digit: string): void => {
    setPin((current) => appendDigit(current, digit));
  };

  const clear = (): void => {
    setPin('');
  };

  const submit = (): void => {
    if (!canSubmitPin(pin, disabled)) {
      return;
    }
    onSubmit(pin);
    setPin('');
  };

  return (
    <div className="pinpad">
      <div className="pinpad__display" aria-label="PIN entry">
        {'•'.repeat(pin.length)}
      </div>
      <div className="pinpad__grid">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className="pinpad__key"
            onClick={() => {
              append(key);
            }}
            disabled={disabled}
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          className="pinpad__key pinpad__key--clear"
          onClick={clear}
          disabled={disabled}
        >
          Clear
        </button>
        <button
          type="button"
          className="pinpad__key"
          onClick={() => {
            append('0');
          }}
          disabled={disabled}
        >
          0
        </button>
        <button
          type="button"
          className="pinpad__key pinpad__key--enter"
          onClick={submit}
          disabled={!canSubmitPin(pin, disabled)}
        >
          Enter
        </button>
      </div>
    </div>
  );
}
